<script lang="ts">
  import { Search, ChevronDown, EyeIcon } from '../icons';
  import { formatPostCode, formatCanadaPostalCode } from 'core-components';

  let inputElement: HTMLInputElement;

  // out
  export let value: string;

  // in
  export let label = '',
    rightLabel = '',
    size: 'small' | 'medium' | 'large' | '2xlarge' = 'large',
    caption = '',
    prefix = '',
    placeholder = '',
    type: 'text' | 'email' | 'tel' | 'password' = 'text',
    error = false,
    errorMessage = '',
    name = '',
    disabled = false,
    success = false,
    isSearch = false,
    isDropdown = false,
    required = false,
    extend = false,
    isBold = false,
    hasFormattingPostalCode = false,
    autoComplete: string | boolean = 'off',
    readonly = false,
    maxlength = 1000;

  // generate unique id for input since
  // crypto.randomUUID() is giving error
  const id =
    'id_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 5);

  function typeAction(node: HTMLInputElement) {
    node.type = type;
  }
</script>

<div
  class="input_container"
  class:extend
  style={isDropdown ? 'background-color: var(--color-neutralWhite);' : ''}
>
  {#if label}
    <label for={id} class="input_label">{label}</label>
  {/if}
  <label
    class="input_wrapper"
    class:is-sm={size === 'small'}
    class:is-md={size === 'medium'}
    class:is-lg={size === 'large'}
    class:is-xl={size === '2xlarge'}
    class:has-icon-left={$$slots.icon}
  >
    {#if prefix}
      <span class="prefix">
        {prefix}
      </span>
    {/if}
    <input
      bind:this={inputElement}
      class="input_field"
      class:error
      class:success
      class:disabled
      class:isBold
      class:search={isSearch}
      class:dropdown={isDropdown}
      class:field={$$slots.field}
      class:has-icon-left={$$slots.icon}
      class:has-icon-right={$$slots.iconRight}
      {name}
      {disabled}
      {maxlength}
      autocomplete={typeof autoComplete === 'string'
        ? autoComplete
        : autoComplete
          ? 'on'
          : 'off'}
      use:typeAction
      bind:value
      {required}
      {id}
      {placeholder}
      {readonly}
      on:input
      on:keyup
      on:blur
      on:focus
      use:formatPostCode={hasFormattingPostalCode && formatCanadaPostalCode}
    />
    {#if isSearch && !$$slots.action && !$$slots.field && !$$slots.icon}
      <span class="search-icon">
        <Search />
      </span>
    {/if}
    {#if isDropdown && !$$slots.action && !$$slots.field && !$$slots.icon}
      <button class="dropdown-icon" on:click>
        <ChevronDown />
      </button>
    {/if}
    {#if name === 'password'}
      <button
        class="password-button"
        tabindex="-1"
        on:click={ev => {
          ev.preventDefault();
          type = type === 'password' ? 'text' : 'password';
          typeAction(inputElement);
        }}
      >
        <EyeIcon />
      </button>
    {/if}
    {#if $$slots.action}
      <button class="action-block">
        <slot name="action" />
      </button>
    {/if}
    {#if $$slots.field}
      <span class="field-block">
        <slot name="field" />
      </span>
    {/if}
    {#if $$slots.icon}
      <span class="icon-block is-left">
        <slot name="icon" />
      </span>
    {/if}
    {#if $$slots.iconRight}
      <span class="icon-block is-right">
        <slot name="iconRight" />
      </span>
    {/if}
    {#if rightLabel}
      <span class="right-label">
        {rightLabel}
      </span>
    {/if}
  </label>

  {#if caption}
    {#if error && errorMessage}
      <p class="input_error-message">{errorMessage}</p>
    {:else}
      <p class="input_help-text">{caption}</p>
    {/if}
  {:else if error && errorMessage}
    <p class="input_error-message">{errorMessage}</p>
  {/if}
</div>

<style lang="scss">
  .input {
    &_container {
      &.extend {
        width: 100%;
      }
    }

    &_label {
      display: block;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 20px;
      letter-spacing: 0.14px;
      text-align: left;
      color: var(--color-neutralBlack, #111111);
    }
    &_wrapper {
      display: flex;
      align-items: center;
      width: 100%;
      padding: 10px var(--spacing-micro);
      color: var(--color-neutralBlack, #111111);
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 21px;
      letter-spacing: 0.14px;
      background-color: var(--color-neutralWhite);
      background-clip: padding-box;
      border: 1px solid var(--color-neutralExtraLight);
      border-radius: var(--spacing-atom);
      transition: border-color 0.15s ease-in-out;
      margin: var(--spacing-nano) 0;
      padding: 0 var(--spacing-micro);

      .prefix {
        border-right: 1px solid var(--color-neutralExtraLight);
        padding: 10px 0;
        padding-right: var(--spacing-micro);
        margin-right: var(--spacing-micro);
      }

      &:focus-within {
        outline: none;
        border-color: var(--color-primary);
        background-color: var(--color-primary-light);
      }
      &:has(.error) {
        border-color: var(--color-error);
        background-color: var(--color-error-light);
      }
      &:has(.success) {
        border-color: var(--color-success);
        background-color: var(--color-success-light);
      }
      &:has(.disabled) {
        background-color: var(--color-background-light);
        cursor: not-allowed;
      }
      &:has(.dropdown[disabled]) {
        background: inherit;
        color: inherit;
        cursor: pointer;
        pointer-events: none;
      }
      &:has(.search) {
        padding-left: var(--spacing-medium);
      }
      &:has(.dropdown),
      &:has(.search) {
        padding-right: var(--spacing-medium);
      }
      &:has(.field) {
        padding-left: var(--spacing-huge);
      }
      //Required for backwards compatibility
      &.has-icon-left {
        padding-left: var(--spacing-large, 40px);
      }
      &:has(.has-icon-left) {
        padding-left: var(--spacing-large, 40px);
      }
      &:has(.icon-block.is-left) {
        padding-left: var(--spacing-large, 40px);
      }
      &:has(.has-icon-right) {
        padding-right: var(--spacing-large, 40px);
      }

      &.is-sm {
        font-size: 12px;

        .input_field {
          padding: 3px 0;
        }
      }

      &.is-md {
        padding: 7px 0;

        .input_field {
          font-size: 12px;
        }
      }

      &.is-lg {
        font-size: 14px;

        .input_field {
          padding: 10px 0;
        }
      }

      &.is-xl {
        font-size: 16px;

        .input_field {
          padding: 16px 0;
        }
      }
    }
    &_field {
      display: block;
      width: 100%;
      -webkit-text-fill-color: inherit;
      color: inherit;
      font-size: inherit;
      font-style: normal;
      font-weight: 400;
      line-height: 21px;
      opacity: 1;
      letter-spacing: 0.14px;
      border: unset !important;
      outline: unset !important;
      background: unset !important;

      &::placeholder {
        color: #787e87;
      }

      &.disabled {
        color: #787e87;
        cursor: not-allowed;
        &.isBold {
          color: var(--color-neutralGreyDark);
        }
      }

      &.dropdown[disabled] {
        background: inherit;
        color: inherit;
        cursor: pointer;
        pointer-events: none;
      }
    }

    // Variants
    &_wrapper {
      position: relative;

      .search-icon {
        position: absolute;
        top: 50%;
        left: var(--spacing-nano);
        transform: translateY(-50%);
      }

      .dropdown-icon {
        position: absolute;
        border: none;
        background: none;
        cursor: pointer;
        top: 55%;
        right: var(--spacing-nano);
        transform: translateY(-50%);
      }

      .action-block {
        position: absolute;
        border: none;
        background: none;
        cursor: pointer;
        top: 0;
        right: 0;
        bottom: 0;
        padding: var(--spacing-micro);
        border-left: 1px solid var(--color-neutralExtraLight);
      }

      .field-block {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        padding: var(--spacing-micro);
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        letter-spacing: 0.14px;
        width: var(--spacing-2xlarge);
        color: var(--color-neutralGreyLight);
        border-right: 1px solid var(--color-neutralExtraLight);
      }

      .icon-block {
        position: absolute;
        top: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        padding: var(--spacing-micro);

        &.is-left {
          left: 0;
          padding-right: var(--spacing-nano);
        }

        &.is-right {
          right: 0;
          padding-left: var(--spacing-nano);
        }
      }

      .right-label {
        color: var(--color-neutralGreyLight);
        font-weight: 500;
      }
    }

    &_help-text,
    &_error-message {
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px;
      letter-spacing: 0.12px;
      text-align: left;
      padding: 0;
      margin: 0;
      margin-top: -2.5px;
    }

    &_error-message {
      color: var(--destructive-foreground);
    }
  }
  .password-button {
    background: none;
    border: none;
    cursor: pointer;
  }
</style>
